/**
 * @fileoverview System-web adapter from template records to the shared preview shell.
 * @module system-web/template-builder
 *
 * Responsibilities:
 * - Order and canonicalize system template forms.
 * - Provide system-web's preview contacts renderer and data-resolution seams.
 *
 * Dependencies:
 * - @firesquirrel/inspection-renderer for canonical conversion and preview.
 * - @firesquirrel/ui-admin ContactsModulePreview for session-mode contacts.
 *
 * Architecture:
 * - Host adapter; preview interaction and form rendering remain package-owned.
 *
 * Notes:
 * - Contacts are kept in component memory for the lifetime of this preview.
 */
import { useCallback, useMemo, useState } from 'react';
import { ContactsModulePreview } from '@firesquirrel/ui-admin';
import type { ContactsModuleConfig, TemplateBuilderForm, TemplateBuilderTemplate } from '@firesquirrel/ui-admin';
import type { ContactEntry, InspectionForm, InspectionRule } from '@firesquirrel/core-domain';
import {
  TemplatePreviewShell,
  toCanonicalForm,
  type CanonicalFormAdapterInput,
  type InspectionRendererProps,
} from '@firesquirrel/inspection-renderer';
import { buildPreviewAttachmentPicker, buildPreviewResolveContext } from '../../lib/buildPreviewAdapters';

/**
 * @contract SystemTemplatePreviewModeProps
 * Purpose: Supply the system template and its Dexie-loaded form records.
 * Fields: template, forms.
 * Rules: Forms are ordered and converted before entering the shared preview.
 * Sync: No writes occur from the preview adapter.
 */
export interface SystemTemplatePreviewModeProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
}

/**
 * @component SystemTemplatePreviewMode
 * Purpose: Adapt system template records to the shared device-aware preview.
 * Props: SystemTemplatePreviewModeProps.
 * Behavior: Converts forms and injects the system contacts session renderer.
 * Data Source: Local, supplied by the system template editor.
 * Constraints: Does not own form presentation or conduct state.
 * Performance: Canonical forms are memoized by form map and template refs.
 */
export function SystemTemplatePreviewMode({ template, forms }: SystemTemplatePreviewModeProps) {
  const canonicalForms = useMemo<InspectionForm[]>(() => {
    const refs = [...(template.formRefs ?? [])].sort((left, right) => left.order - right.order);
    return refs.flatMap((ref) => {
      const form = forms.get(ref.formId);
      if (!form) return [];
      const adapterInput: CanonicalFormAdapterInput = {
        _id: String(form._id ?? ref.formId),
        name: String(form.name ?? ref.formId),
        description: form.description,
        fields: (form.fields ?? []) as CanonicalFormAdapterInput['fields'],
        layout: (form.layout ?? { sections: [] }) as CanonicalFormAdapterInput['layout'],
        display: form.display as CanonicalFormAdapterInput['display'],
        rules: form.rules as CanonicalFormAdapterInput['rules'],
        version: typeof form.version === 'number' ? form.version : 1,
        updatedAt: typeof form.updatedAt === 'string' ? form.updatedAt : '',
        formKind: form.formKind ?? ref.formKind,
        moduleConfig: form.moduleConfig ?? ref.moduleConfig,
      };
      return [toCanonicalForm(adapterInput)];
    });
  }, [forms, template.formRefs]);
  const [moduleContacts, setModuleContacts] = useState<ContactEntry[]>([]);
  const attachmentPicker = useMemo(() => buildPreviewAttachmentPicker(), []);
  const resolveContext = useMemo(() => buildPreviewResolveContext(), []);
  const renderModule = useCallback<NonNullable<InspectionRendererProps['renderModule']>>((form, context) => {
    if (form.moduleConfig?.moduleType !== 'contacts') return undefined;
    return (
      <ContactsModulePreview
        config={form.moduleConfig as ContactsModuleConfig}
        initialContacts={moduleContacts.length > 0 ? moduleContacts : context.contacts}
        onContactsChange={(contacts) => {
          setModuleContacts(contacts);
          context.onContactsChange(contacts, []);
        }}
        mode="session"
      />
    );
  }, [moduleContacts]);

  return (
    <TemplatePreviewShell
      forms={canonicalForms}
      templateName={template.name}
      templateId={template._id}
      packageLabel={template.templateTypeName || undefined}
      templateRules={(template.rules ?? []) as unknown as InspectionRule[]}
      attachmentPicker={attachmentPicker}
      resolveContext={resolveContext}
      renderModule={renderModule}
    />
  );
}

export default SystemTemplatePreviewMode;
